import { useState } from "react";
import Subheadline from "../Elements/Subheadline";
import ProfileCard from "../Cards/CardProfile";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import Popup from "../Elements/Popup";
import InputText from "../Inputs/InputText";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateProfileLoginDetails, deleteUser } from "../../api";
import { useAuthContext } from "../../context/context";
import { useLanguage } from "../../context/LanguageContext";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

const TabPersonalInfo = ({
  personalInfoFields,
  handleFieldUpdate,
  handleUpdatePersonalDetails,
  setToast,
}) => {
  const { userData, setUserData } = useAuthContext();
  const { copy } = useLanguage();
  const [popupType, setPopupType] = useState(null);
  const navigate = useNavigate();

  // Email change form
  const emailSchema = z.object({
    email: z
      .string()
      .nonempty(copy.profile.errors.email_required)
      .email(copy.profile.errors.invalid_email),
  });
  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: emailErrors },
    reset: resetEmailForm,
  } = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const handleUpdateEmail = async (data) => {
    const userId = userData?.id;
    if (!userId)
      return setToast({
        message: copy.profile.errors.user_id_missing,
        type: "error",
      });
    try {
      const response = await updateProfileLoginDetails(userId, {
        email: data.email,
      });
      console.log("response", response);
      if (response.requiresVerification) {
        setToast({
          message: copy.profile.errors.email_verification_required,
          type: "info",
        });
        setPopupType(null);
      } else {
        setUserData((prev) => ({ ...prev, email: data.email }));
        setToast({
          message: copy.profile.errors.email_updated,
          type: "success",
        });
        setPopupType(null);
      }
      resetEmailForm();
    } catch (error) {
      console.log("Email update error:", error);
      setToast({
        message: copy.profile.errors.email_change_failed,
        type: "error",
      });
    }
  };

  const handleDeleteAccount = async () => {
    const userId = userData?.id;
    if (!userId)
      return setToast({
        message: copy.profile.errors.user_id_missing,
        type: "error",
      });
    try {
      await deleteUser(userId);
      setToast({
        message: copy.profile.errors.account_deleted,
        type: "success",
      });
      navigate("/register?delete=true");
    } catch {
      setToast({
        message: copy.profile.errors.account_delete_failed,
        type: "error",
      });
    }
  };

  return (
    <>
      <Subheadline
        subheadline={copy.profile.tab_1}
        description={copy.profile.tab_1_description}
      />
      <ProfileCard
        title={copy.profile.card_personal_title}
        fields={personalInfoFields}
        onUpdate={handleFieldUpdate}
        buttonCTA={copy.dropdown.update}
        onButtonClick={handleUpdatePersonalDetails}
      />
      <ProfileCard
        title={copy.profile.cards.change_email.title}
        buttonCTA={copy.profile.form.button_change_email}
        onButtonClick={() => setPopupType("email")}
      >
        <div className="flex flex-col gap-2">
          {userData.isVerified ? (
            <span
              title={copy.profile.form.email_verified}
              className="flex items-center mb-1 gap-1 text-sm"
            >
              <FaCheckCircle className="text-green-700" />{" "}
              {copy.profile.form.email_verified}
            </span>
          ) : (
            <span
              title={copy.profile.form.email_not_verified}
              className="flex items-center mb-1 gap-1 text-sm"
            >
              <FaTimesCircle className="text-red-500" />{" "}
              {copy.profile.form.email_not_verified}
            </span>
          )}
          <div className="w-80 !bg-accent-light">
            <div className="px-4 py-2 rounded-lg text-sm bg-accent-light card-base-border">
              {userData.email}
            </div>
          </div>
        </div>
      </ProfileCard>

      {/* Delete Account Card */}
      <ProfileCard
        title={copy.profile.cards.delete_account.title}
        description={copy.profile.cards.delete_account.description}
      >
        <BtnSecondaryM
          onClickLink={() => setPopupType("delete")}
          buttonCTA={copy.profile.form.button_delete_account}
        />
      </ProfileCard>

      {/* Email Change Popup */}
      {popupType === "email" && (
        <Popup
          isOpen={true}
          onClose={() => {
            setPopupType(null);
            resetEmailForm();
          }}
          title={copy.profile.form.title_change_email}
          description={copy.profile.form.description_change_email}
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={() => {
            setPopupType(null);
            resetEmailForm();
          }}
          buttonPrimaryCTA={copy.profile.form.button_confirm}
          onClickPrimaryLink={handleSubmitEmail(handleUpdateEmail)}
        >
          <form
            className="flex flex-col gap-4 mt-4"
            onSubmit={handleSubmitEmail(handleUpdateEmail)}
          >
            <InputText
              label={copy.login.form.label_email}
              placeholder={copy.login.form.placeholder_email}
              {...registerEmail("email")}
              error={emailErrors.email?.message}
            />
          </form>
        </Popup>
      )}

      {/* Delete Account Popup */}
      {popupType === "delete" && (
        <Popup
          isOpen={popupType === "delete"}
          onClose={() => setPopupType(null)}
          title={copy.profile.popup.delete_title}
          description={copy.profile.popup.delete_description}
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={() => setPopupType(null)}
          buttonPrimaryCTA={copy.dropdown.delete}
          onClickPrimaryLink={handleDeleteAccount}
        />
      )}
    </>
  );
};

TabPersonalInfo.propTypes = {
  personalInfoFields: PropTypes.array.isRequired,
  handleFieldUpdate: PropTypes.func.isRequired,
  handleUpdatePersonalDetails: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
};

export default TabPersonalInfo;
